// Shared mm/pt-unit drawing helpers for the vector PDF renderers
// (pdfHeader/pdfFooter/pdf*Table/pdfBracketTree/pdfToc). Every renderer in
// this folder works in the same two units jsPDF itself exposes: "mm" for
// all coordinates/sizes (the doc is always created with `unit: 'mm'`) and
// "pt" for font sizes (what `setFontSize` takes) - these helpers convert
// CSS-px-shaped values (ported straight from TournamentPrintableTable.js's
// inline styles) into those two units so every renderer's numbers still
// trace back to the original CSS.
export const PX_TO_MM = 0.264583;
export const PT_TO_MM = 0.352778;

// CSS px -> pt, at the same 96dpi/72pt assumption browsers use - lets every
// renderer keep reusing TournamentPrintableTable.js's own `fontSize: "13px"`
// etc. literals instead of inventing a parallel pt scale.
export const px2pt = (px) => px * 0.75;
export const px2mm = (px) => px * PX_TO_MM;

export const lineHeightMm = (fontSizePt, multiplier = 1.3) => fontSizePt * PT_TO_MM * multiplier;

// Sets font size (pt) and wraps `text` to `maxWidthMm`, returning both the
// wrapped lines and the block height so a renderer's measure() and draw()
// can share one calculation and never drift apart.
export const wrapText = (pdf, text, maxWidthMm, fontSizePt, multiplier = 1.3) => {
    pdf.setFontSize(fontSizePt);
    const lines = pdf.splitTextToSize(String(text ?? ""), Math.max(maxWidthMm, 1));
    const lh = lineHeightMm(fontSizePt, multiplier);
    return { lines, lineHeightMm: lh, heightMm: Math.max(lines.length, 1) * lh };
};

// Draws pre-wrapped (or single-line) text top-down from (x, y) and returns
// the block height actually used.
export const drawText = (pdf, text, x, y, { fontSizePt = 10, color, style = "normal", align = "left", maxWidthMm, multiplier = 1.3 } = {}) => {
    pdf.setFont(pdf.__pdfFontName || "PTSans", style);
    pdf.setFontSize(fontSizePt);
    if (color) pdf.setTextColor(color);
    const lines = maxWidthMm ? pdf.splitTextToSize(String(text ?? ""), maxWidthMm) : [String(text ?? "")];
    const lh = lineHeightMm(fontSizePt, multiplier);
    lines.forEach((line, i) => {
        pdf.text(line, x, y + lh * (i + 0.78), { align });
    });
    return lines.length * lh;
};

export const fillRect = (pdf, x, y, w, h, color) => {
    pdf.setFillColor(color);
    pdf.rect(x, y, w, h, "F");
};

export const strokeRect = (pdf, x, y, w, h, color, lineWidthMm = 0.15) => {
    pdf.setDrawColor(color);
    pdf.setLineWidth(lineWidthMm);
    pdf.rect(x, y, w, h, "S");
};

export const hLine = (pdf, x, y, w, color, lineWidthMm = 0.15) => {
    pdf.setDrawColor(color);
    pdf.setLineWidth(lineWidthMm);
    pdf.line(x, y, x + w, y);
};

export const vLine = (pdf, x, y, h, color, lineWidthMm = 0.15) => {
    pdf.setDrawColor(color);
    pdf.setLineWidth(lineWidthMm);
    pdf.line(x, y, x, y + h);
};

// Small filled+bordered pill used for the "Stage" badge - mirrors
// styles.stageBadge in TournamentPrintableTable.js (background T.border,
// text T.ink, ~4px radius, 2px/8px padding).
export const drawBadge = (pdf, text, x, y, { fontSizePt = 9, bg, color, padXMm = 1.6, padYMm = 0.9, radiusMm = 1 } = {}) => {
    pdf.setFont(pdf.__pdfFontName || "PTSans", "normal");
    pdf.setFontSize(fontSizePt);
    const textW = pdf.getTextWidth(text);
    const h = lineHeightMm(fontSizePt, 1) + padYMm * 2;
    const w = textW + padXMm * 2;
    pdf.setFillColor(bg);
    pdf.roundedRect(x, y, w, h, radiusMm, radiusMm, "F");
    pdf.setTextColor(color);
    pdf.text(text, x + padXMm, y + h / 2 + fontSizePt * PT_TO_MM * 0.32);
    return { width: w, height: h };
};

// Loads any raster image URL (club logos are photos on a separate host,
// not always CORS-friendly) into a PNG data URL via canvas, resolving null
// on any failure - same graceful-degradation shape as the live app's own
// `onError={() => setLogoFailed(true)}` handling for this same image.
export const loadImageAsPngDataUrl = (url) => new Promise((resolve) => {
    if (!url) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
        try {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth || img.width;
            canvas.height = img.naturalHeight || img.height;
            if (!canvas.width || !canvas.height) return resolve(null);
            canvas.getContext("2d").drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        } catch (e) {
            resolve(null);
        }
    };
    img.onerror = () => resolve(null);
    img.src = url;
});

// A single fixed-height, single-line table row - background (header tint
// or zebra stripe), each column's vertically-centered text, and the row's
// bottom border + column separators (border-collapse-style: each row only
// draws its own bottom+verticals, so consecutive rows' edges coincide the
// same way CSS `border-collapse: collapse` does). Callers draw one top
// border once before the first row. `cols[i]` is
// `{ widthMm, text, align, color, bold, padXMm }`.
export const drawTableRow = (pdf, { x, y, totalWidthMm, cols, rowHeightMm, bg, borderColor, fontSizePt = 10, fontName, textColor = "#000000" }) => {
    const font = fontName || pdf.__pdfFontName || "PTSans";
    if (bg) fillRect(pdf, x, y, totalWidthMm, rowHeightMm, bg);

    const textY = y + rowHeightMm / 2 + fontSizePt * PT_TO_MM * 0.32;
    let cx = x;
    cols.forEach((col) => {
        if (col.customDraw) {
            col.customDraw(pdf, cx, y, col.widthMm, rowHeightMm);
            cx += col.widthMm;
            return;
        }
        pdf.setFont(font, col.bold ? "bold" : "normal");
        pdf.setFontSize(col.fontSizePt || fontSizePt);
        // Always set explicitly (never left to whatever the previous draw
        // call happened to leave on the pdf context) - a header row drawn
        // right after a colored banner (e.g. the green card title bar)
        // would otherwise silently inherit that banner's white text color.
        pdf.setTextColor(col.color || textColor);
        const padX = col.padXMm ?? 0;
        const align = col.align || "left";
        let tx = cx + padX;
        if (align === "center") tx = cx + col.widthMm / 2;
        else if (align === "right") tx = cx + col.widthMm - padX;
        if (col.text != null && col.text !== "") pdf.text(String(col.text), tx, textY, { align });
        cx += col.widthMm;
    });

    if (borderColor) {
        pdf.setDrawColor(borderColor);
        pdf.setLineWidth(0.15);
        pdf.line(x, y + rowHeightMm, x + totalWidthMm, y + rowHeightMm);
        let vx = x;
        cols.forEach((col) => {
            pdf.line(vx, y, vx, y + rowHeightMm);
            vx += col.widthMm;
        });
        pdf.line(vx, y, vx, y + rowHeightMm);
    }
    return rowHeightMm;
};

// Draws `dataUrl` clipped to a circle of `diameterMm` centered at (cx, cy) -
// used for the round club-logo avatar (styles.clubLogo's borderRadius: 50%
// in TournamentPrintableTable.js/PrintableToc.js).
export const drawCircularImage = (pdf, dataUrl, cx, cy, diameterMm) => {
    if (!dataUrl) return;
    const r = diameterMm / 2;
    pdf.saveGraphicsState();
    pdf.circle(cx, cy, r, null);
    pdf.clip();
    pdf.discardPath();
    pdf.addImage(dataUrl, "PNG", cx - r, cy - r, diameterMm, diameterMm);
    pdf.restoreGraphicsState();
};
